import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { addCardToQueue, addCardToQueueDisplay } from '../../../../../../store/card/card.action';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  AddButton,
  CancelButton,
  InputContainer,
  ErrorContainer,
  ParallelSection,
  ParallelToggle,
  ParallelInputs
} from './AddCustomCardModal.styles';
import InputBar from '../../../../../../components/input-bar/InputBar';
import CriticalMessage from '../../../../../../components/system/critical-message/CriticalMessage';

export default function AddCustomCardModal({ 
  isOpen, 
  onClose,
  sport,
  year,
  manufacturer,
  series,
  cardType,
  cardTypeCategory
}) {
  const dispatch = useDispatch();
  const [cardNumber, setCardNumber] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [team, setTeam] = useState('');
  const [hasParallel, setHasParallel] = useState(false);
  const [parallelName, setParallelName] = useState('');
  const [parallelColor, setParallelColor] = useState('#FFFFFF');
  const [parallelNumerator, setParallelNumerator] = useState('');
  const [parallelDenominator, setParallelDenominator] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setCardNumber('');
    setPlayerName('');
    setTeam('');
    setHasParallel(false);
    setParallelName('');
    setParallelColor('#FFFFFF');
    setParallelNumerator('');
    setParallelDenominator('');
    setError('');
    onClose();
  };

  const handleAddToQueue = () => {
    // Validate required fields
    if (!cardNumber.trim()) {
      setError('Card number is required');
      return;
    }

    if (!playerName.trim()) {
      setError('Player name is required');
      return;
    }

    // Validate parallel if enabled
    if (hasParallel && !parallelName.trim()) {
      setError('Parallel name is required when adding parallel information');
      return;
    }

    // Validate numbered parallel
    if (hasParallel && (parallelNumerator.trim() || parallelDenominator.trim())) {
      const numerator = parseInt(parallelNumerator, 10);
      const denominator = parseInt(parallelDenominator, 10);
      
      if (isNaN(numerator) || isNaN(denominator) || numerator <= 0 || denominator <= 0) {
        setError('Both numerator and denominator must be valid positive numbers');
        return;
      }
    }

    // Validate hex color
    const hexColor = parallelColor.trim();
    const isValidHex = hexColor && (/^#[0-9A-Fa-f]{6}$/.test(hexColor) || /^#[0-9A-Fa-f]{3}$/.test(hexColor));
    const finalColor = isValidHex ? hexColor : '#FFFFFF';

    // Create card object
    const card = {
      cardNumber: cardNumber.trim(),
      name: [playerName.trim()],
      team: team.trim() ? [team.trim()] : [],
      cardAttributes: [],
      collectedCards: [{
        cardParallelName: hasParallel ? parallelName.trim() : 'BASE',
        cardColor: hasParallel ? finalColor : '#FFFFFF',
        cardQty: 1,
        cardPrintNumber: hasParallel && parallelNumerator.trim() ? parseInt(parallelNumerator, 10) : null,
        cardSupply: hasParallel && parallelDenominator.trim() ? parseInt(parallelDenominator, 10) : null
      }]
    };

    // Add to queue
    dispatch(addCardToQueue(sport, year, manufacturer, series, cardType, cardTypeCategory, card, 1));
    dispatch(addCardToQueueDisplay(sport, year, manufacturer, series, cardType, cardTypeCategory, card, 1));

    handleClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={handleClose} className='widget-bg'>
      <ModalContainer onClick={(e) => e.stopPropagation()} className='panel-bg'>
        <ModalHeader>
          <ModalTitle>Add Card to Custom Category</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <InputContainer>
            <InputBar
              type="text"
              label="Card Number"
              value={cardNumber}
              updateValue={setCardNumber}
              id="card-number-input"
            />
          </InputContainer>
          <InputContainer>
            <InputBar
              type="text"
              label="Player Name"
              value={playerName}
              updateValue={setPlayerName}
              id="player-name-input"
            />
          </InputContainer>
          <InputContainer>
            <InputBar
              type="text"
              label="Team (Optional)"
              value={team}
              updateValue={setTeam}
              id="team-input"
            />
          </InputContainer>

          <ParallelSection>
            <ParallelToggle>
              <input
                type="checkbox"
                id="has-parallel"
                checked={hasParallel}
                onChange={(e) => setHasParallel(e.target.checked)}
              />
              <label htmlFor="has-parallel">Add Parallel Information</label>
            </ParallelToggle>

            {hasParallel && (
              <ParallelInputs>
                <InputContainer>
                  <InputBar
                    type="text"
                    label="Parallel Name"
                    value={parallelName}
                    updateValue={setParallelName}
                    id="parallel-name-input"
                  />
                </InputContainer>
                <InputContainer>
                  <InputBar
                    type="text"
                    label="Color (Hex, e.g., #FF0000)"
                    value={parallelColor}
                    updateValue={setParallelColor}
                    id="parallel-color-input"
                  />
                </InputContainer>
                <InputContainer>
                  <InputBar
                    type="text"
                    label="Numbered: Numerator (Optional)"
                    value={parallelNumerator}
                    updateValue={setParallelNumerator}
                    id="parallel-numerator-input"
                  />
                </InputContainer>
                <InputContainer>
                  <InputBar
                    type="text"
                    label="Numbered: Denominator (Optional)"
                    value={parallelDenominator}
                    updateValue={setParallelDenominator}
                    id="parallel-denominator-input"
                  />
                </InputContainer>
              </ParallelInputs>
            )}
          </ParallelSection>

          {error && (
            <ErrorContainer>
              <CriticalMessage text={error} color='red' />
            </ErrorContainer>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <AddButton 
              onClick={handleAddToQueue}
              disabled={!cardNumber.trim() || !playerName.trim()}
            >
              Add to Queue
            </AddButton>
            <CancelButton onClick={handleClose}>
              Cancel
            </CancelButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}

